import { useMemo } from 'react'
import type { JSX } from 'react'

import { unparse } from 'papaparse'

import { CheckIcon, CriticalIcon, Flex, Link, Text } from '@buffer-mono/popcorn'

import type { Service } from '../../graphqlTypes'

import { isError, isSuccess, isValid } from './helpers'
import type { ListItem } from './helpers'
import type { UserFriendlyCsvLine } from '../CsvUploader/validators'

import styles from './StatusCounters.module.css'

export const StatusCounters = ({
  items,
  channelService,
  isCreatingPosts,
  isPostCreationComplete,
  selectedSaveAction,
}: {
  items: ListItem[]
  channelService: Service
  isCreatingPosts: boolean
  isPostCreationComplete: boolean
  selectedSaveAction: 'post' | 'draft'
}): JSX.Element => {
  const validItemCount = useMemo(() => {
    return items.filter((item) => isValid(item)).length
  }, [items])
  const successItemCount = useMemo(() => {
    return items.filter((item) => isSuccess(item)).length
  }, [items])
  const errorItemCount = useMemo(() => {
    return items.filter((item) => isError(item)).length
  }, [items])

  const validItemCountText = useMemo(() => {
    return `${validItemCount} valid ${selectedSaveAction}${
      validItemCount !== 1 ? 's' : ''
    }`
  }, [validItemCount, selectedSaveAction])

  const successItemCountText = useMemo(() => {
    return `${successItemCount} ${selectedSaveAction}${
      successItemCount !== 1 ? 's' : ''
    } ${selectedSaveAction === 'draft' ? 'saved' : 'scheduled'}${
      isCreatingPosts ? '...' : ''
    }`
  }, [successItemCount, isCreatingPosts, selectedSaveAction])

  const errorItemCountText = useMemo(() => {
    return `${errorItemCount} ${selectedSaveAction}${
      errorItemCount !== 1 ? 's' : ''
    } with errors${isCreatingPosts ? '...' : ''}`
  }, [errorItemCount, isCreatingPosts, selectedSaveAction])

  // Construct a CSV of failed posts using papaparse with the following columns: Text, Image URL, Tags, Errors
  const shouldShowFailedPostsCsv = useMemo(() => {
    const hasErrors = errorItemCount > 0
    return hasErrors && isPostCreationComplete
  }, [errorItemCount, isPostCreationComplete])

  const failedPostsCsv = useMemo(() => {
    const failedItems = items.filter(isError)
    if (failedItems.length === 0 || !isPostCreationComplete) return ''

    const data: UserFriendlyCsvLine[] = failedItems.map((item) =>
      Object.assign(
        {
          Text: item.data.text || '',
          'Image URL': item.data.media?.photo || '',
        },
        channelService === 'pinterest' && {
          'Board Name': item.data.board?.name || '',
        },
        {
          Tags: item.data.tags.map((tag) => tag.name).join(','),
          'Posting Time':
            item.data.postingTime === 'Next Available'
              ? 'Add to queue'
              : item.data.postingTime,
          Errors: item.errors?.join('|') || '',
        },
      ),
    )

    return unparse(data, {
      quotes: true,
    })
  }, [items, channelService, isPostCreationComplete])

  const onDownloadFailedPostsCsv = useMemo(
    () => (): void => {
      const blob = new Blob([failedPostsCsv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'failed-posts.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
    [failedPostsCsv],
  )

  return (
    <Flex align="center" gap="lg" className={styles.statusCounters}>
      {isCreatingPosts || isPostCreationComplete ? (
        <Text icon={<CheckIcon color="success" />}>{successItemCountText}</Text>
      ) : (
        <Text icon={<CheckIcon />}>{validItemCountText}</Text>
      )}
      {errorItemCount > 0 && (
        <Text icon={<CriticalIcon color="critical" />}>
          {errorItemCountText}
          {shouldShowFailedPostsCsv && (
            <>
              .{' '}
              <Link
                as="button"
                onClick={onDownloadFailedPostsCsv}
                className={styles.downloadLink}
              >
                Download CSV with post errors
              </Link>
            </>
          )}
        </Text>
      )}
    </Flex>
  )
}
